import { Box, IconButton, Text } from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import { MdOutlineLogout, MdWarningAmber } from "react-icons/md";
import { RiFileEditLine, RiForbid2Line, RiLockPasswordLine, RiQrScan2Line } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import CTable from "../../../components/Table/table";
import { brandPalette } from "../../../constants/brandPalette";
import {
  openCreateStudent,
  openStudentReportModel,
  openUserQRCodeModel,
} from "../../../modules/admin-modules/models/Actions";
import {
  clearSessionRequest,
  deleteStudentRequest,
  getInactiveStudentsRequest,
  restrictStudentRequest,
  resendPasswordRequest,
} from "../../../modules/admin-modules/students/Actions";

const C = {
  primary: brandPalette.primary,
  secondary: brandPalette.accentDark,
  border: brandPalette.stone,
};

const iconBtnProps = {
  variant: "ghost",
  border: "0",
  size: "sm",
  color: C.primary,
  _hover: { bg: "rgba(91, 13, 22, 0.08)", color: C.secondary },
};

const InactiveStudentsTable = ({ setAdd, setSelectedData, searchTerm, inactivePeriod }) => {
  const inactiveStudents = useSelector((state) => state.students.inactiveStudents);
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.students.isLoading);
  const count = useSelector((state) => state.students.count);
  const isRestrict = useSelector((state) => state.students.isRestrict);
  const isSuspend = useSelector((state) => state.students.isSuspend);

  const [offset, setPage] = useState(0);
  const [size, setPerPage] = useState(50);
  const [pageNumber, setPageNumber] = useState(0);
  const [selected, setSelected] = useState({ id: "" });

  const list = useMemo(
    () => (Array.isArray(inactiveStudents) ? inactiveStudents : []),
    [inactiveStudents]
  );

  useEffect(() => {
    setPageNumber(0);
  }, [searchTerm, inactivePeriod]);

  useEffect(() => {
    const timeId = setTimeout(() => {
      const flag = inactivePeriod === "oneWeek" ? 1 : undefined;
      dispatch(getInactiveStudentsRequest(pageNumber + 1, searchTerm || "", flag));
    }, 200);
    return () => clearTimeout(timeId);
  }, [pageNumber, searchTerm, inactivePeriod, dispatch]);

  useEffect(() => {
    setSelectedData({});
    setSelected({ id: "" });
  }, [pageNumber]);

  const viewData = (row) => {
    const plain = list.find((s) => s.id === row?.id) ?? row;
    setSelectedData(plain);
  };

  const Data = list.map((data, index) => ({
    ...data,
    index: `#${pageNumber * size + index + 1}`,
    id: data?.id,
    student_name: (
      <Text
        cursor="pointer"
        fontWeight="700"
        color={C.secondary}
        onClick={(e) => {
          e.stopPropagation();
          setSelectedData(data);
          dispatch(openStudentReportModel());
        }}
      >
        {data?.name}
      </Text>
    ),
    group_name: data?.group?.name,
    phone: data?.phone,
    code: (
      <IconButton
        {...iconBtnProps}
        aria-label="QR code"
        fontSize="20px"
        icon={<RiQrScan2Line />}
        onClick={(e) => {
          e.stopPropagation();
          setSelectedData(data);
          dispatch(openUserQRCodeModel());
        }}
      />
    ),
    delete: (
      <IconButton
        {...iconBtnProps}
        aria-label="Suspend or restore student"
        fontSize="20px"
        icon={
          <MdWarningAmber color={data?.deleted_at == null ? "#9b2c2c" : "#1e5c3a"} />
        }
        onClick={(e) => {
          e.stopPropagation();
          setSelectedData(data);
          dispatch(deleteStudentRequest(data?.id));
        }}
        isLoading={selected.id === data?.id ? isSuspend : false}
      />
    ),
    restrict: (
      <IconButton
        {...iconBtnProps}
        aria-label="Restrict access"
        fontSize="20px"
        isLoading={selected.id === data?.id ? isRestrict : false}
        icon={<RiForbid2Line color={data?.restrict === 0 ? "#9b2c2c" : "#1e5c3a"} />}
        onClick={(e) => {
          e.stopPropagation();
          setSelectedData(data);
          const formData = new FormData();
          formData.append("_method", "PUT");
          if (data?.restrict === 0) {
            formData.append("restrict", 0);
          } else {
            formData.append("restrict", 1);
          }
          dispatch(
            restrictStudentRequest({
              id: data?.id,
              formData,
            })
          );
        }}
      />
    ),
    clearSession: (
      <IconButton
        {...iconBtnProps}
        aria-label="Clear session"
        fontSize="20px"
        icon={<MdOutlineLogout />}
        onClick={(e) => {
          e.stopPropagation();
          setSelectedData(data);
          dispatch(clearSessionRequest(data?.id));
        }}
      />
    ),
    edit: (
      <IconButton
        {...iconBtnProps}
        aria-label="Edit student"
        fontSize="20px"
        icon={<RiFileEditLine />}
        onClick={(e) => {
          e.stopPropagation();
          setSelectedData(data);
          setAdd(false);
          dispatch(openCreateStudent());
        }}
      />
    ),
    resendPassword: (
      <IconButton
        {...iconBtnProps}
        aria-label="Resend password"
        fontSize="20px"
        icon={<RiLockPasswordLine />}
        onClick={(e) => {
          e.stopPropagation();
          setSelectedData(data);
          setSelected({ id: data?.id });
          dispatch(resendPasswordRequest(data?.id));
        }}
        isLoading={selected.id === data?.id ? isLoading : false}
      />
    ),
  }));

  const columns = [
    { Header: "#", accessor: "index" },
    { Header: "Student name", accessor: "student_name" },
    { Header: "Code", accessor: "user_name" },
    { Header: "Group", accessor: "group_name" },
    { Header: "QR", accessor: "code" },
    { Header: "Phone", accessor: "phone" },
    { Header: "Suspend", accessor: "delete" },
    { Header: "Clear session", accessor: "clearSession" },
    { Header: "Restrict", accessor: "restrict" },
    { Header: "Resend password", accessor: "resendPassword" },
    { Header: "Edit", accessor: "edit" },
  ];

  const totalPage = Math.max(1, Math.ceil((count || 0) / size));

  return (
    <Box px={{ base: 4, md: 8 }} pb={10} w="100%">
      <Box
        bg="var(--surface-raised)"
        border="1px solid"
        borderColor={C.border}
        borderRadius="16px"
        overflow="hidden"
        boxShadow="sm"
      >
        <CTable
          selectedData={viewData}
          Columns={columns}
          Data={Data}
          path="openModel"
          Title=""
          subTitle=""
          btnTitle=""
          placeHolder=""
          noSearchBar
          noFilter
          footerBtnTitle
          filterList
          addButton
          addButtonTitle=""
          setPage={setPage}
          setPerPage={setPerPage}
          currentpage={pageNumber}
          setPageNumber={setPageNumber}
          noSecondSearchBar
          isLoading={isLoading}
          perPage={size}
          totalPage={totalPage}
          wrapperProps={{ maxH: "unset", overflow: "auto" }}
          tableProps={{ bg: "transparent", borderRadius: "0", mb: 0 }}
          theadProps={{ bg: "var(--surface-muted)", color: "var(--text-primary)" }}
        />
      </Box>
    </Box>
  );
};

export default InactiveStudentsTable;
